import { type LinksFunction, json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { SpotlightsSection } from '#app/components/spotlights/SpotlightsSection.tsx'
import {
  jsonLdScript,
  organizationJsonLd,
  webSiteJsonLd,
} from '#app/helpers/agentDiscovery/jsonLd.ts'
import { metaTitleAndDescription } from '#app/helpers/metaTags.helper.ts'
import { fetchSpotlightsLandingTeasers } from '#app/models/content.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'

import ContactFormSection from './_index+/components/ContactFormSection.tsx'
import DownloadAppSection from './_index+/components/DownloadAppSection.tsx'
import FirstSection from './_index+/components/FirstSection.tsx'
import FiveSection from './_index+/components/FiveSection.tsx'
import Footer from './_index+/components/Footer.tsx'
import FourSection from './_index+/components/FourSection.tsx'
import HelpSection from './_index+/components/HelpSection.tsx'
import NavbarLanding from './_index+/components/NavbarLanding.tsx'
import SixSection from './_index+/components/SixSection.tsx'
import ThirdSection from './_index+/components/ThirdSection.tsx'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.siteUrl) return []

  return [
    ...metaTitleAndDescription({
      title: 'Clube do Tennis',
      description: 'A comunidade do tenista: rankings, desafios e perfis.',
      content_type: 'website',
      canonicalUrl: data.siteUrl,
    }),
    jsonLdScript(organizationJsonLd(data.siteUrl)),
    jsonLdScript(webSiteJsonLd(data.siteUrl)),
  ]
}

export const links: LinksFunction = () => {
  return [
    { rel: 'manifest', href: '/site.webmanifest' },
    {
      rel: 'preload',
      fetchpriority: 'high',
      as: 'image',
      href: '/images/lp/background-lp-low-res.png',
      type: 'image/png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      href: '/favicons/apple-touch-icon.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/favicons/favicon-16x16.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/favicons/favicon-32x32.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
      href: '/favicons/android-chrome-192x192.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '512x512',
      href: '/favicons/android-chrome-512x512.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      href: '/favicon.ico',
    },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { items, total } = await fetchSpotlightsLandingTeasers({ limit: 4 })
  return json({
    spotlights: items,
    spotlightsTotal: total,
    siteUrl: getDomainUrl(request),
  })
}

export default function Index() {
  const { spotlights, spotlightsTotal } = useLoaderData<typeof loader>()

  return (
    <div className="app-landing-page bg-white">
      <NavbarLanding />
      <FirstSection />
      <DownloadAppSection />
      <div id="como-funciona">
        <ThirdSection />
      </div>
      <FourSection />
      <FiveSection />
      <SixSection />
      <SpotlightsSection items={spotlights} total={spotlightsTotal} />
      <HelpSection />
      <div id="contato">
        <ContactFormSection />
      </div>
      <Footer />
    </div>
  )
}
